import { styled } from '@linaria/react'
import React, { FC } from 'react'
import { OptionButton } from '../ui/OptionButton'
import { Typography } from '../ui/Typography'
import TonIcon from '../svgr/TonIcon'
import { colors } from '../styles/colors'
import { FormattedMessage } from 'react-intl'
import { formatTon } from '../utils/formatTon'

export const VoteOptionWithMeta: FC<{
  option: {
    percent: number
    userVotes?: number
    userTon?: number
    choice: string
    voteCount: string
    choiceIndex: number
  }
  checked: boolean
  disabled: boolean
  onChange: (choiceIndex: number) => void
  profitabilityPercent: number
  isRightAnswer?: boolean | null
  isProfitabilityShow?: boolean
}> = ({
  checked,
  disabled,
  isProfitabilityShow,
  isRightAnswer,
  onChange,
  option,
  profitabilityPercent,
}) => {
  return (
    <OptionButton
      id={option.choiceIndex.toString()}
      value={option.choiceIndex}
      onChange={onChange}
      disabled={disabled}
      checked={checked}
      isRightAnswer={isRightAnswer}
    >
      <OptionContent>
        <TopSection>
          <Typography
            fontSize={13}
            fontWeight={700}
            font="manrope"
            style={{ color: colors.textPrimary }}
          >
            {option.choice}
          </Typography>

          <MetricsContainer>
            <PercentContainer checked={checked}>
              <Typography
                fontSize={checked ? 14 : 12}
                fontWeight={600}
                font="roboto"
              >
                {option.percent}%
              </Typography>
            </PercentContainer>

            <Separator />

            <TonContainer>
              <Typography
                fontSize={14}
                fontWeight={400}
                font="roboto"
                style={{ color: colors.textPrimary }}
              >
                {formatTon(option.voteCount)}
              </Typography>

              <TonIcon />
            </TonContainer>
          </MetricsContainer>
        </TopSection>

        {isProfitabilityShow && (
          <ProfitabilitySection>
            <Typography
              fontSize={12}
              fontWeight={400}
              font="roboto"
              style={{ color: colors.grey700 }}
            >
              <FormattedMessage
                id="amountInput.profitability"
                defaultMessage="Доходность:"
              />
            </Typography>

            <Typography
              fontSize={12}
              fontWeight={600}
              font="roboto"
              style={{ color: colors.textPrimary }}
            >
              {profitabilityPercent.toFixed(2)}%
            </Typography>
          </ProfitabilitySection>
        )}

        {Boolean(option.userVotes) && Boolean(option.userTon) && (
          <UserVotesSection>
            <Typography
              fontSize={10}
              fontWeight={400}
              font="roboto"
              style={{ color: colors.grey600 }}
            >
              <FormattedMessage
                id="youVoted"
                defaultMessage="Вы проголосовали:"
              />{' '}
            </Typography>

            <Typography
              fontSize={10}
              fontWeight={400}
              font="roboto"
              style={{ color: colors.textPrimary }}
            >
              <FormattedMessage
                id="votes.count"
                defaultMessage="{count, plural, one {# голос} few {# голоса} many {# голосов} other {# голосов}}"
                values={{ count: formatTon(String(option.userVotes)) }}
              />
            </Typography>

            <Typography
              fontSize={10}
              fontWeight={400}
              font="roboto"
              style={{ color: colors.textSecondary }}
            >
              {' / '}
            </Typography>

            <Typography
              fontSize={10}
              fontWeight={400}
              font="roboto"
              style={{ color: colors.textPrimary }}
            >
              {formatTon(String(option.userTon ?? ''))} TON
            </Typography>
          </UserVotesSection>
        )}
      </OptionContent>
    </OptionButton>
  )
}

const OptionContent = styled.div`
  display: grid;
  grid-gap: 2px;
  gap: 2px;
`

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const MetricsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: center;
`

const UserVotesSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const PercentContainer = styled.div<{ checked: boolean }>`
  color: ${props => (props.checked ? colors.textSecondary : colors.textMuted)};
  text-align: right;
`

const Separator = styled.div`
  width: 1px;
  height: 20px;
  background: ${colors.grey200};
`

const TonContainer = styled.div`
  display: grid;
  gap: 4px;
  grid-gap: 4px;
  grid-template-columns: 1fr auto;
  min-width: 38px;
  justify-items: flex-end;
  align-items: center;
`

const ProfitabilitySection = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  margin-top: 4px;
`
